// coach_tools.js — the AI coach's tool surface. Pure functions (no React, no
// network) so the tool contract is unit-testable:
//
//   COACH_TOOLS                    JSON-schema tool definitions sent to Claude
//   runReadTool(name, input, ctx)  executes read-only tools against app state
//   resolveExercise(name, ...)     fuzzy name → exercise (state library, then catalog)
//   applyProgramChanges(...)       applies an approved change set to a program (immutable)
//   buildProgramFromSpec(...)      turns an approved new-program spec into a program object
//                                  (progression schemes + week cycle from progression.js)
//   contextBlockFor(ctx, state)    "Ask about this" seed for the coach
//   weeklyReviewPrompt / WEEKLY_REVIEW_SCHEMA / normalizeReview
//                                  structured weekly review with applicable suggestions,
//   reviewWindow / reviewData      on ONE window: the 7 days ending yesterday
//
// Write tools (propose_program_changes / propose_new_program) never mutate
// anything by themselves: the UI renders them as approval cards and only the
// user's tap applies them.

import { exerciseHistory, completedSessions, trainingSnapshot, repMaxes, volumeReport, weekStart, deloadAdvice, muscleSets, indexExercises, TRACKED_MUSCLES, VOLUME_LANDMARKS } from './analytics.js';
import { prescriptionFor, localDateStr } from './utils.jsx';
import { schemeOf, schemeLabel, prescribe, programWeekInfo, rowsSummary, tmFor, tmKeyOf, tmLifts, WAVES, SCHEMES, roundTo, withCurrentWeek, cycleOf, nextWorkout } from './progression.js';
import { generateWorkout, FOCUS_OPTIONS, GOAL_OPTIONS, GEN_MUSCLES, EQUIPMENT_TAGS, GEN_LIBRARY, INJURY_LABEL, injuryTags, resolveEquipment } from './generator.js';

const round1 = (x) => Math.round(x * 10) / 10;
const fmtDate = (ms) => localDateStr(ms); // the user's calendar day, not UTC

// ——— Tool definitions ———————————————————————————————————————————————

const changeItem = {
  type: 'object',
  additionalProperties: false,
  properties: {
    op: { type: 'string', enum: ['update', 'swap', 'add', 'remove', 'set_tm', 'set_scheme', 'pin_week'], description: "update = change sets/reps/weight/rest of an exercise already in the day; swap = replace an exercise with another; add = add a new exercise to the day; remove = delete an exercise from the day; set_tm = set the training max (tm_lb) of a percent-scheme lift — the only way to change the loads of a percent lift; set_scheme = change how an exercise progresses (scheme + its parameters, see propose_new_program; tm_lb for a new percent lift); pin_week = make week N of the program's cycle the current one (week, 1-based; day and exercise may be '')." },
    day: { type: 'string', description: 'Name of the program day, exactly as returned by get_program (e.g. "Upper A"). For set_tm: any day that contains the lift.' },
    exercise: { type: 'string', description: 'Existing exercise name in that day (for update/swap/remove/set_tm), or the exercise to add (for add).' },
    lift: { type: 'string', description: 'For set_tm: the training-max lift as named in get_program training_maxes_lb (defaults to the exercise).' },
    tm_lb: { type: 'number', minimum: 0, description: 'For set_tm: the new training max in pounds.' },
    new_exercise: { type: 'string', description: 'For swap only: the replacement exercise name. Prefer names returned by search_exercises.' },
    sets: { type: 'integer', minimum: 1, maximum: 12 },
    rep_min: { type: 'integer', minimum: 1, maximum: 100 },
    rep_max: { type: 'integer', minimum: 1, maximum: 100 },
    weight_lb: { type: 'number', minimum: 0, description: 'Target working weight in pounds. Ignored (and reported) for percent-scheme lifts, whose loads come from the training max — use set_tm for those.' },
    rest_sec: { type: 'integer', minimum: 15, maximum: 600 },
    scheme: { type: 'string', enum: Object.keys(SCHEMES), description: 'For set_scheme.' },
    wave: { type: 'string', enum: Object.keys(WAVES), description: "For set_scheme 'percent'." },
    inc_lb: { type: 'number', minimum: 1, maximum: 20, description: "For set_scheme 'linear'." },
    tier: { type: 'integer', minimum: 1, maximum: 3, description: "For set_scheme 'gzcl'." },
    top_reps: { type: 'integer', minimum: 1, maximum: 12, description: "For set_scheme 'rpe_topset'." },
    rpe: { type: 'number', minimum: 6, maximum: 10, description: "For set_scheme 'rpe_topset'." },
    backoffs: { type: 'integer', minimum: 0, maximum: 6, description: "For set_scheme 'rpe_topset'." },
    backoff_reps: { type: 'integer', minimum: 1, maximum: 15, description: "For set_scheme 'rpe_topset'." },
    mev: { type: 'integer', minimum: 1, maximum: 10, description: "For set_scheme 'rp_sets'." },
    mrv: { type: 'integer', minimum: 1, maximum: 12, description: "For set_scheme 'rp_sets'." },
    week: { type: 'integer', minimum: 1, maximum: 52, description: 'For pin_week: the cycle week (1-based) to make current.' },
    reason: { type: 'string', description: 'One short sentence shown to the user explaining this specific change.' },
  },
  required: ['op', 'day', 'exercise', 'reason'],
};

export const COACH_TOOLS = [
  {
    name: 'get_program',
    description: "Return the user's active training program: the current week (number, label, deload), training maxes, and every day with its exercises — each with its progression scheme and THIS week's prescription (sets × reps @ lb). Call this before proposing any program change.\n\nSchemes (how loads move — respect them): 'double' = reps first, then +weight when every set hits the top of the rep range (weight_lb is the target); 'linear' = fixed sets × reps, +weight after every successful session, −10 % after repeated misses; 'percent' = loads are percentages of a training max (training_max_lb) set per week by a wave (e.g. 5/3/1) with '+' sets (as many reps as possible) that move the TM — never set weight_lb on these, change the TM with set_tm instead; 'rpe_topset' = one top set at a target RPE, back-offs a % below, load follows the lifter's e1RM; 'gzcl' = tiered stages (5×3+ → 6×2+ → 10×1+); 'rp_sets' = hypertrophy block adding a set a week up to MRV, RIR 3 → 0, then deload. In a deload week everything is lighter on purpose — don't treat it as regression.",
    input_schema: { type: 'object', additionalProperties: false, properties: {}, required: [] },
  },
  {
    name: 'get_exercise_history',
    description: "Return the user's logged history for one exercise: per-session best set, estimated 1RM, sets and volume, plus rep-max records. Use it to ground advice about a specific lift in real numbers.",
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        exercise: { type: 'string', description: 'Exercise name (fuzzy matched).' },
        limit: { type: 'integer', minimum: 1, maximum: 40, description: 'Most recent N sessions (default 12).' },
      },
      required: ['exercise'],
    },
  },
  {
    name: 'get_recent_workouts',
    description: 'Return the most recent completed workouts with every working set (weight lb × reps @ RPE).',
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: { count: { type: 'integer', minimum: 1, maximum: 10 } },
      required: ['count'],
    },
  },
  {
    name: 'get_weekly_volume',
    description: 'Return hard sets per muscle group for the current week and the 4-week average, with MEV/MAV/MRV volume landmarks.',
    input_schema: { type: 'object', additionalProperties: false, properties: {}, required: [] },
  },
  {
    name: 'search_exercises',
    description: 'Search the 870+ exercise library by name, target muscle, and/or equipment. Use it to find valid substitutions before proposing a swap or add.',
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        query: { type: 'string', description: 'Words in the exercise name (optional).' },
        muscle: { type: 'string', description: 'Primary muscle, e.g. Chest, Back, Shoulders, Quads, Hamstrings, Glutes, Calves, Biceps, Triceps, Core.' },
        equipment: { type: 'string', description: 'e.g. Barbell, Dumbbell, Machine, Cable, Bodyweight, Kettlebell, Band, Smith.' },
      },
      required: [],
    },
  },
  {
    name: 'build_workout',
    description: "Build a one-off workout for today with the app's deterministic workout builder: it picks exercises from the lifter's recovery per muscle, this week's sets vs. target, equipment and injuries, and suggests loads from their e1RMs at a target RPE. Returns the plan (exercises, sets x reps @ load, why each was picked) and shows it as a card with a Start button. Use it when the user wants a workout now, isn't following their program today, or asks what to train.\n\nWhen the user states today's equipment, pain or exercises to skip, pass them (equipment, avoid, exclude_exercises); the saved profile's equipment, injuries and avoided exercises always apply as well. Describe ONLY exercises present in the result, and tell the user what was left out and why (the result's left_out field).",
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        minutes: { type: 'integer', minimum: 15, maximum: 120, description: 'Time budget (default 45).' },
        focus: { type: 'string', enum: FOCUS_OPTIONS.map(f => f.id), description: "'auto' = most recovered, most behind on volume. 'muscles' = the muscles listed." },
        muscles: { type: 'array', items: { type: 'string', enum: GEN_MUSCLES }, description: "For focus 'muscles'." },
        goal: { type: 'string', enum: GOAL_OPTIONS.map(g => g.id), description: 'Defaults to the lifter profile goal.' },
        equipment: { type: 'array', items: { type: 'string', enum: EQUIPMENT_TAGS.map(t => t.id) }, description: "Equipment available for THIS workout when the user states it (e.g. 'dumbbells only' → ['dumbbell'], 'dumbbells and a bench' → ['dumbbell','bench'], 'no equipment' → []). Omit to use the lifter's saved equipment. Every exercise in the plan will use only this equipment." },
        avoid: { type: 'array', items: { type: 'string', enum: Object.keys(INJURY_LABEL) }, description: "Body areas to spare today when the user mentions pain or tightness (e.g. 'lower back is tight' -> ['lowerBack'], 'sore knee' -> ['knees']). Added to the injuries in their profile." },
        exclude_exercises: { type: 'array', items: { type: 'string' }, description: "Exercises the user doesn't want today, by name (e.g. ['Deadlift', 'Bulgarian Split Squat'])." },
      },
      required: [],
    },
  },
  {
    name: 'propose_program_changes',
    description: "Propose edits to the active program. Nothing changes until the user taps Apply on the card this renders. The app validates every change first: if any can't be applied you get an error listing why — fix those and call again. Batch related edits into one call. Every exercise/day name must match get_program output (or search_exercises for new exercises). To change how a lift progresses use set_scheme; to move the plan to another week of its cycle use pin_week. For a 'percent' lift, loads come from its training max: propose op 'set_tm' with tm_lb (e.g. lower a TM ~10 % after missed + sets, or set one from a recent best: TM ≈ 90 % of the estimated 1RM) — a weight_lb on a percent lift is not applied. For 'double'/'linear' lifts weight_lb sets the next target.",
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        title: { type: 'string', description: 'Short card title, e.g. "Hip-friendly leg day".' },
        changes: { type: 'array', minItems: 1, maxItems: 20, items: changeItem },
      },
      required: ['title', 'changes'],
    },
  },
  {
    name: 'get_exercise_guide',
    description: "Return the exercise library's technique guide for one exercise: target and secondary muscles, equipment, level, mechanic and the step-by-step instructions, plus the lifter's most recent working sets of it. Use it for form, cue and technique questions and ground your cues in these steps.",
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: { exercise: { type: 'string', description: 'Exercise name (fuzzy matched against the library).' } },
      required: ['exercise'],
    },
  },
  {
    name: 'propose_new_program',
    description: 'Propose a complete new program (for "build me a program" requests). Rendered as a card; the user taps Create to add it and make it active. Use exercise names from search_exercises where possible.\n\n'
      + "Each exercise may run a progression scheme (default 'double' = reps first, then weight):\n"
      + "- 'linear': fixed sets x rep_min, +inc_lb every successful session (novices; squat, bench, deadlift, press).\n"
      + "- 'percent': loads from a training max on a wave. wave = " + Object.entries(WAVES).map(([k, w]) => `${k} (${w.label}, ${w.weeks.length} wk)`).join(', ') + ". Optional tm_of = another lift whose training max it rides (e.g. a bbb 5x10 supplemental on the main lift). Training maxes start at 90 % of the lifter's logged e1RM.\n"
      + "- 'rpe_topset': one top set of top_reps at rpe, then backoffs x backoff_reps about 10 % lighter.\n"
      + "- 'gzcl': tier 1 (5x3+ -> 6x2+ -> 10x1+), 2 (3x10 -> 3x8 -> 3x6) or 3 (3x15+).\n"
      + "- 'rp_sets': hypertrophy mesocycle from mev up to mrv sets, RIR 3 -> 0, then deload.\n"
      + 'Give the program a cycle (week labels, deload weeks) that matches its waves or mesocycle, e.g. 4 weeks with a deload for w531, 5 weeks (4 + deload) for rp_sets. Omit cycle for plain double/linear programs.',
    input_schema: {
      type: 'object', additionalProperties: false,
      properties: {
        name: { type: 'string' },
        summary: { type: 'string', description: 'One or two sentences on who it is for and how it progresses.' },
        weeks: { type: 'integer', minimum: 1, maximum: 24 },
        days: {
          type: 'array', minItems: 1, maxItems: 7,
          items: {
            type: 'object', additionalProperties: false,
            properties: {
              name: { type: 'string' },
              weekday: { type: 'string', enum: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
              exercises: {
                type: 'array', minItems: 1, maxItems: 12,
                items: {
                  type: 'object', additionalProperties: false,
                  properties: {
                    exercise: { type: 'string' },
                    sets: { type: 'integer', minimum: 1, maximum: 10 },
                    rep_min: { type: 'integer', minimum: 1, maximum: 60 },
                    rep_max: { type: 'integer', minimum: 1, maximum: 60 },
                    rest_sec: { type: 'integer', minimum: 15, maximum: 600 },
                    weight_lb: { type: 'number', minimum: 0 },
                    scheme: { type: 'string', enum: Object.keys(SCHEMES) },
                    wave: { type: 'string', enum: Object.keys(WAVES), description: "For 'percent'." },
                    tm_of: { type: 'string', description: "For 'percent': ride another lift's training max (exercise name)." },
                    inc_lb: { type: 'number', minimum: 1, maximum: 20, description: "For 'linear': weight added per successful session (default 5, 2.5 for small lifts)." },
                    tier: { type: 'integer', minimum: 1, maximum: 3, description: "For 'gzcl'." },
                    top_reps: { type: 'integer', minimum: 1, maximum: 12, description: "For 'rpe_topset'." },
                    rpe: { type: 'number', minimum: 6, maximum: 10, description: "For 'rpe_topset': top-set RPE." },
                    backoffs: { type: 'integer', minimum: 0, maximum: 6, description: "For 'rpe_topset'." },
                    backoff_reps: { type: 'integer', minimum: 1, maximum: 15, description: "For 'rpe_topset'." },
                    mev: { type: 'integer', minimum: 1, maximum: 10, description: "For 'rp_sets': starting sets." },
                    mrv: { type: 'integer', minimum: 1, maximum: 12, description: "For 'rp_sets': maximum sets." },
                    tm_lb: { type: 'number', minimum: 0, description: "For 'percent': starting training max in lb when the lifter told you one (otherwise it is estimated from their log)." },
                  },
                  required: ['exercise', 'sets', 'rep_min', 'rep_max', 'rest_sec'],
                },
              },
            },
            required: ['name', 'weekday', 'exercises'],
          },
        },
        cycle: {
          type: 'object', additionalProperties: false,
          description: 'Week structure for programs with waves or a mesocycle. The plan advances one week per full rotation of the days.',
          properties: {
            repeat: { type: 'boolean', description: 'true = run the cycle again after the last week.' },
            weeks: { type: 'array', minItems: 1, maxItems: 16, items: { type: 'object', additionalProperties: false, properties: { label: { type: 'string' }, deload: { type: 'boolean' } }, required: ['label'] } },
          },
          required: ['weeks'],
        },
      },
      required: ['name', 'summary', 'weeks', 'days'],
    },
  },
];

export const WRITE_TOOLS = new Set(['propose_program_changes', 'propose_new_program']);
// Tools that run immediately (the model gets the result) and also render a card.
export const CARD_READ_TOOLS = new Set(['build_workout']);

/**
 * Runs the workout builder for a build_workout call → plan (generator.js shape)
 * plus plan.constraints: what was honoured and what was left out. Today's
 * equipment, areas to spare and skipped exercises add to the saved profile
 * (equipment, injuries, avoided exercises): the same constraints the non-AI
 * builder sheet uses, so both surfaces agree.
 */
export function buildWorkoutPlan(state, input = {}, now = Date.now()) {
  const opts = {};
  const profile = state?.profile || {};
  if (input.minutes > 0) opts.minutes = Math.max(15, Math.min(120, Math.round(input.minutes)));
  if (input.focus) opts.focus = input.focus;
  if (Array.isArray(input.muscles) && input.muscles.length) { opts.muscles = input.muscles; if (!input.focus) opts.focus = 'muscles'; }
  if (input.goal) opts.goal = input.goal;
  if (Array.isArray(input.equipment)) {
    const ok = new Set(EQUIPMENT_TAGS.map(t => t.id));
    opts.equipment = input.equipment.filter(t => ok.has(t));
  }
  if (!opts.minutes) opts.minutes = state?.prefs?.generator?.minutes || 45;
  // Areas to spare today, on top of the profile's injuries (the generator reads profile.injuries).
  const todayAreas = [...new Set((Array.isArray(input.avoid) ? input.avoid : []).filter(a => INJURY_LABEL[a]))];
  const areas = [...new Set([...injuryTags(profile.injuries), ...todayAreas])];
  const injuriesText = [profile.injuries, ...todayAreas.map(a => INJURY_LABEL[a])].filter(Boolean).join(', ');
  // Exercises to skip, by name → ids (the builder's library, then the lifter's own).
  const skipped = [];
  const pool = [...GEN_LIBRARY, ...((state && state.exercises) || [])];
  for (const name of (Array.isArray(input.exclude_exercises) ? input.exclude_exercises : []).slice(0, 12)) {
    const n = String(name || '').trim();
    if (!n) continue;
    const hits = pool.filter(e => e && e.name && (nameScore(n, e.name) >= 0.6 || norm(e.name).includes(norm(n))));
    if (hits.length) skipped.push({ name: n, ids: [...new Set(hits.map(e => e.id))] });
  }
  if (skipped.length) opts.exclude = [...new Set(skipped.flatMap(x => x.ids))];
  const plan = generateWorkout({ ...state, profile: { ...profile, injuries: injuriesText } }, opts, now);
  // What the constraints took out, named so the coach can say it.
  const have = new Set(plan.equipment || []);
  const noEquipment = ['barbell', 'dumbbell', 'cable', 'machine', 'pullup', 'kettlebell'].filter(t => !have.has(t));
  const spared = GEN_LIBRARY.filter(e => (e.stress || []).some(t => areas.includes(t)) && (e.needs || []).every(t => have.has(t))
    && (!plan.muscles || !plan.muscles.length || plan.muscles.includes(e.group))).map(e => e.name);
  const avoidedSaved = (profile.avoidExercises || []).map(id => pool.find(e => e.id === id)?.name).filter(Boolean);
  plan.constraints = {
    equipment: [...have],
    equipmentSource: Array.isArray(input.equipment) ? 'today' : 'profile',
    noEquipment,
    areas, areasToday: todayAreas,
    sparedExamples: spared.slice(0, 4),
    skipped: skipped.map(x => x.name),
    avoidedSaved,
  };
  return plan;
}

const KIT_WORD = { barbell: 'barbell', dumbbell: 'dumbbell', cable: 'cable', machine: 'machine', pullup: 'pull-up bar', kettlebell: 'kettlebell', bench: 'bench' };

/** Plain list of what a built plan left out (for the coach and the card). */
export function planLeftOut(plan) {
  const c = plan && plan.constraints;
  if (!c) return [];
  const out = [];
  if (c.noEquipment.length && c.noEquipment.length < 6) out.push(`No ${c.noEquipment.map(t => KIT_WORD[t] || t).join(', ')} work (${c.equipmentSource === 'today' ? 'not available today' : 'not in your equipment'})`);
  if (c.areas.length) out.push(`Nothing that loads your ${c.areas.map(a => INJURY_LABEL[a]).join(' or ')}${c.sparedExamples.length ? ` (so no ${c.sparedExamples.slice(0, 3).join(', ')})` : ''}`);
  if (c.skipped.length) out.push(`Skipped as asked: ${c.skipped.join(', ')}`);
  if (c.avoidedSaved.length) out.push(`On your avoid list: ${c.avoidedSaved.join(', ')}`);
  return out;
}

/** What the model sees of a built workout. */
export function planForModel(plan) {
  const leftOut = planLeftOut(plan);
  return {
    name: plan.name, est_minutes: plan.estMinutes, total_sets: plan.totalSets, goal: plan.goal,
    exercises: plan.items.map(it => ({
      exercise: it.name, muscle: it.muscle, equipment: it.equipment || null, sets: it.sets, reps: it.repMin === it.repMax ? it.repMin : `${it.repMin}-${it.repMax}`,
      load_lb: it.load > 0 ? it.load : (it.bodyweight ? 'bodyweight' : 'find a weight (no history)'), target_rpe: it.rpe, rest_sec: it.rest, why: it.why,
    })),
    equipment_used: plan.equipment,
    ...(plan.constraints ? { left_out: leftOut.length ? leftOut : ['Nothing: every exercise in the library fit their equipment and joints.'] } : {}),
    notes: plan.notes,
    shown_to_user: 'As a card with a Start button. Name only exercises listed above, never one that is not in this result. In one short sentence tell the user what you left out and why (left_out). Do not repeat every set; highlight what matters.',
  };
}

// ——— Exercise resolution ————————————————————————————————————————————

const norm = (s) => (s || '').toLowerCase().replace(/\(.*?\)/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
const tokens = (s) => norm(s).split(' ').filter(t => t && !['the', 'a', 'with', 'on', 'medium', 'grip'].includes(t));

// Token-overlap score in [0,1] with a bonus for an exact normalized match.
function nameScore(query, name) {
  const q = norm(query), n = norm(name);
  if (!q || !n) return 0;
  if (q === n) return 1;
  const qt = tokens(query), nt = new Set(tokens(name));
  if (!qt.length) return 0;
  const hit = qt.filter(t => nt.has(t) || nt.has(t.replace(/s$/, '')) || nt.has(t + 's')).length;
  const cover = hit / qt.length;
  const precision = hit / Math.max(1, nt.size);
  return 0.75 * cover + 0.25 * precision - (n.includes(q) ? 0 : 0.02);
}

// Best match among the user's library first (so history lines up), then the catalog.
export function resolveExercise(name, exercises = [], catalog = null) {
  let best = null, bestScore = 0;
  for (const e of exercises) {
    const s = nameScore(name, e.name);
    if (s > bestScore) { best = { ...e, source: 'library' }; bestScore = s; }
  }
  if (bestScore >= 0.99) return best;
  if (catalog) {
    for (const e of catalog) {
      if (e.category && e.category !== 'strength') continue;
      const s = nameScore(name, e.name) - 0.01; // tie → prefer the library entry
      if (s > bestScore) { best = { ...e, source: 'catalog' }; bestScore = s; }
    }
  }
  return bestScore >= 0.6 ? best : null;
}

// The catalog entry (with steps) for a library or catalog exercise: by id, else by name.
function catalogEntry(ex, catalog) {
  if (!ex || !catalog) return null;
  const byId = catalog.find(e => e.id === ex.id);
  if (byId) return byId;
  const m = resolveExercise(ex.name, [], catalog);
  return m ? catalog.find(e => e.id === m.id) || null : null;
}

// ——— Read tools ———————————————————————————————————————————————————

export function programForModel(program, exercises = [], sessions = [], now = Date.now()) {
  if (!program) return { error: 'No active program. Suggest creating one with propose_new_program.' };
  const wk = programWeekInfo({ program, sessions, now });
  const engine = (program.days || []).some(d => (d.exercises || []).some(pe => schemeOf(pe) !== 'double'));
  const tms = tmLifts(program);
  const manualDeload = !!(program.deloadWeek && program.deloadWeek === weekStart(now));
  return {
    name: program.name,
    split: program.split || null,
    periodized_week: program.periodization?.weeks?.length
      ? `${(program.periodization.currentWeek ?? 0) + 1} of ${program.periodization.weeks.length}`
      : null,
    current_week: engine || program.cycle ? {
      number: wk.index + 1,
      label: wk.label,
      ...(wk.length > 1 ? { cycle_week: `${wk.week + 1} of ${wk.length}` } : {}),
      deload: wk.deload || manualDeload,
      ...(manualDeload ? { deload_note: 'The user switched on a deload week: sessions are ~40 % fewer sets and ~10 % lighter.' } : {}),
    } : (manualDeload ? { deload: true, deload_note: 'The user switched on a deload week.' } : null),
    training_maxes_lb: tms.length ? Object.fromEntries(tms.map(t => [t.name, t.tm > 0 ? t.tm : null])) : undefined,
    next_workout: (() => {
      const nx = nextWorkout({ programs: [program], activeProgramId: program.id, sessions, activeSessionId: null }, program.id, now);
      return nx.day ? { day: nx.day.name, planned_for: nx.plannedFor || null, today: nx.today, trained_today: nx.trainedToday, note: nx.reason || undefined, ...(nx.missedDays.length ? { missed_last_week: nx.missedDays.map(m => m.name) } : {}) } : null;
    })(),
    days: program.days.map(d => ({
      name: d.name,
      weekday: d.day || null,
      exercises: d.exercises.map(pe => {
        const rx = prescriptionFor(program, pe);
        const scheme = schemeOf(pe);
        const out = {
          exercise: rx.name || pe.name,
          scheme,
          sets: rx.sets, rep_min: rx.repMin, rep_max: rx.repMax,
          weight_lb: pe.targetWeight || 0, rest_sec: rx.rest,
          ...(rx.rpeLast ? { target_rpe: rx.rpeLast } : {}),
        };
        if (scheme !== 'double') {
          out.scheme_rule = schemeLabel(pe, 'lb');
          const plan = prescribe({ program, pe, weekIndex: wk.index, history: sessions, units: 'lb' });
          if (plan && plan.rows && plan.rows.length) out.this_week = rowsSummary(plan.rows, 'lb');
          if (plan && plan.note) out.this_week_note = plan.note;
          if (scheme === 'percent') {
            const tm = tmFor(program, pe);
            out.training_max_lb = tm > 0 ? tm : null;
            const lift = tms.find(t => t.key === tmKeyOf(pe));
            if (lift && lift.name !== out.exercise) out.training_max_of = lift.name;
            delete out.weight_lb; // loads come from the TM
            if (!(tm > 0)) out.needs_training_max = true;
          }
        }
        return out;
      }),
    })),
  };
}

export function runReadTool(name, input, { state, program, catalog }) {
  switch (name) {
    case 'get_program':
      return programForModel(program, state.exercises, state.sessions || []);
    case 'get_exercise_history': {
      const ex = resolveExercise(input.exercise, state.exercises, null);
      if (!ex) return { error: `No logged exercise matches "${input.exercise}".` };
      const h = exerciseHistory(state, ex.id);
      if (!h.length) return { exercise: ex.name, sessions: [], note: 'No logged sessions yet.' };
      const rm = repMaxes(state, ex.id);
      return {
        exercise: ex.name,
        total_sessions: h.length,
        best_e1rm_lb: round1(Math.max(...h.map(p => p.e1rm))),
        rep_max_records_lb: Object.fromEntries(Object.entries(rm).map(([k, v]) => [`${k}RM`, v.w])),
        sessions: h.slice(-(input.limit || 12)).map(p => ({
          date: fmtDate(p.date), best_set: `${p.bestW} lb × ${p.bestReps}${p.topRpe ? ' @RPE ' + p.topRpe : ''}`,
          e1rm_lb: round1(p.e1rm), working_sets: p.sets, volume_lb: Math.round(p.volume),
        })),
      };
    }
    case 'get_recent_workouts': {
      const byId = Object.fromEntries((state.exercises || []).map(e => [e.id, e.name]));
      const done = completedSessions(state).slice(-(input.count || 3)).reverse();
      return done.map(s => {
        const prog = state.programs.find(p => p.id === s.programId);
        const day = prog?.days.find(d => d.id === s.dayId);
        const groups = {};
        for (const st of s.sets || []) {
          const nm = st.performedName || byId[st.exerciseId] || st.exerciseName || 'Exercise';
          (groups[nm] = groups[nm] || []).push(`${st.type === 'warmup' ? 'W ' : ''}${st.w}×${st.reps}${st.rpe ? '@' + st.rpe : ''}`);
        }
        return {
          date: fmtDate(s.startedAt), day: day?.name || s.name || 'Workout',
          duration_min: Math.round((s.completedAt - s.startedAt) / 60000),
          notes: s.notes || undefined,
          exercises: Object.entries(groups).map(([k, v]) => `${k}: ${v.join(', ')}`),
        };
      });
    }
    case 'build_workout':
      return planForModel(buildWorkoutPlan(state, input));
    case 'get_weekly_volume':
      return volumeReport(state).map(v => ({ muscle: v.muscle, this_week: v.thisWeek, avg_4wk: v.avg4, mev: v.mev, mav: v.mav, mrv: v.mrv, status: v.status }));
    case 'get_exercise_guide': {
      const ex = resolveExercise(input.exercise, state.exercises || [], catalog);
      if (!ex) return { error: `No exercise matches "${input.exercise}". Try search_exercises.` };
      const entry = catalogEntry(ex, catalog);
      const h = exerciseHistory(state, ex.id);
      const last = h.length ? h[h.length - 1] : null;
      return {
        exercise: entry?.name || ex.name,
        primary_muscles: entry?.primary || (ex.group ? [ex.group] : []),
        secondary_muscles: entry?.secondary || [],
        equipment: entry?.equipment || ex.equipment || null,
        level: entry?.level || null,
        mechanic: entry?.mechanic || ex.mechanic || null,
        steps: entry?.steps?.length ? entry.steps : undefined,
        ...(entry?.steps?.length ? {} : { note: 'No written instructions in the library for this exercise; rely on standard technique and say so.' }),
        lifter_last_session: last ? { date: fmtDate(last.date), best_set: `${last.bestW} lb x ${last.bestReps}${last.topRpe ? ' @RPE ' + last.topRpe : ''}`, e1rm_lb: round1(last.e1rm) } : null,
      };
    }
    case 'search_exercises': {
      const pool = [...(state.exercises || []), ...((catalog || []).filter(e => e.category === 'strength'))];
      const seen = new Set();
      const q = input.query ? norm(input.query) : '';
      const res = pool.filter(e => {
        if (seen.has(e.name)) return false;
        if (input.muscle && !((e.primary || [e.group]).some(m => norm(m) === norm(input.muscle)))) return false;
        if (input.equipment && norm(e.equipment) !== norm(input.equipment)) return false;
        if (q && nameScore(q, e.name) < 0.5) return false;
        seen.add(e.name);
        return true;
      }).slice(0, 25).map(e => ({ name: e.name, muscle: e.group, equipment: e.equipment, mechanic: e.mechanic, in_user_library: !e.catalog && !String(e.id).startsWith('fx_') }));
      return res.length ? res : { results: [], note: 'No matches — try a broader query.' };
    }
    default:
      return { error: `Unknown tool ${name}` };
  }
}

// Compact context injected into the system prompt so the coach starts grounded.
// opts.review: for the weekly review, which brings its own one-window numbers
// (drops the calendar-week volume line so two windows never mix).
export function coachContext(state, program, now = Date.now(), opts = {}) {
  const snap = trainingSnapshot(state, now);
  const lines = [];
  lines.push(`Today: ${new Date(now).toDateString()}. Units the user sees: ${state.prefs?.units || 'lb'} (all tool data is in lb).`);
  if (state.user?.name) lines.push(`User's name: ${state.user.name}.`);
  if (state.profile) {
    const p = state.profile;
    lines.push(`Profile: goal=${p.goal || '?'}, experience=${p.experience || '?'}, days/week=${p.daysPerWeek || '?'}, session length=${p.sessionMinutes || '?'} min, equipment=${(p.equipment || []).join('/') || '?'}${p.injuries ? `, injuries/limitations: ${p.injuries}` : ''}.`);
  }
  lines.push(`Active program: ${program ? program.name : 'none'}.`);
  if (program) {
    const nx = nextWorkout({ ...state, programs: [program], activeProgramId: program.id }, program.id, now);
    if (nx.day) lines.push(`Next workout (same as the Home screen): ${nx.day.name}${nx.reason ? ` — ${nx.reason}` : ''}.`);
    const tl = tmLifts(program).filter(t => t.tm > 0);
    if (tl.length) lines.push(`Training maxes (lb): ${tl.map(t => `${t.name} ${t.tm}`).join(', ')}.`);
  }
  const bw = (state.bodyEntries || []).filter(e => e && e.date && e.weight > 0).sort((a, b) => (a.date < b.date ? -1 : 1));
  if (bw.length) {
    const last = bw[bw.length - 1];
    const cut = localDateStr(now - 28 * 86400000);
    const old = [...bw].reverse().find(e => e.date <= cut);
    lines.push(`Body weight: ${last.weight} on ${last.date}${old ? ` (${last.weight - old.weight >= 0 ? '+' : ''}${round1(last.weight - old.weight)} since ${old.date})` : ''}.`);
  }
  const todayStr = localDateStr(now);
  const ci = (state.sessions || []).filter(x => x.checkin && localDateStr(x.startedAt || now) === todayStr).pop();
  if (ci) lines.push(`Today's pre-workout check-in: ${ci.checkin}${ci.lighter ? ' (chose a lighter session)' : ''}.`);
  lines.push(`Completed workouts: ${snap.sessionsTotal} total, ${snap.sessionsLast4Weeks} in the last 4 weeks; weekly streak ${snap.streakWeeks}.`);
  const r = snap.readiness;
  lines.push(`Readiness (on-device model): ${r.score == null ? 'calibrating' : `${r.score}/100 (${r.label})`} — ${r.reasons.join(' ')}`);
  if (snap.deload?.recommend) lines.push(`Deload flag: YES — ${snap.deload.reasons.join(' ')}`);
  const lifts = snap.lifts.slice(0, 12).map(l =>
    `${l.name}: last ${l.lastBest} on ${fmtDate(l.lastDate)}, e1RM ${l.e1rm} (best ${l.bestE1rm})` +
    `${l.trendPctPerWeek != null ? `, trend ${l.trendPctPerWeek >= 0 ? '+' : ''}${l.trendPctPerWeek.toFixed(1)}%/wk` : ''}${l.plateau ? ', PLATEAU' : ''}`);
  if (lifts.length) lines.push('Main lifts:\n- ' + lifts.join('\n- '));
  const vol = snap.volume.filter(v => v.avg4 > 0 || v.thisWeek > 0)
    .map(v => `${v.muscle} ${v.thisWeek} this wk / ${v.avg4} avg (MEV ${v.mev}, MRV ${v.mrv})${v.status !== 'ok' ? ' ' + v.status.toUpperCase() : ''}`);
  if (vol.length && !opts.review) lines.push('Weekly hard sets: ' + vol.join('; '));
  return lines.join('\n');
}

// ——— Applying approved changes ————————————————————————————————————————

let _n = 0;
const localId = (p) => p + Date.now().toString(36).slice(-4) + (++_n).toString(36) + Math.random().toString(36).slice(2, 5);

function withPrescription(program, dayId, peId, patch) {
  const per = program.periodization;
  const periodized = per && Array.isArray(per.weeks) && per.weeks.length > 0;
  const inline = {};
  const rx = {};
  if (patch.sets != null) rx.sets = patch.sets;
  if (patch.repMin != null) rx.repMin = patch.repMin;
  if (patch.repMax != null) rx.repMax = patch.repMax;
  if (patch.rest != null) rx.rest = patch.rest;
  if (patch.targetWeight != null) inline.targetWeight = patch.targetWeight;
  const days = program.days.map(d => d.id !== dayId ? d : {
    ...d, exercises: d.exercises.map(e => e.id !== peId ? e : { ...e, ...inline, ...(periodized ? {} : rx) }),
  });
  if (!periodized || !Object.keys(rx).length) return { ...program, days };
  const cur = typeof per.currentWeek === 'number' ? per.currentWeek : 0;
  const weeks = per.weeks.map((wk, i) => {
    if (i < cur) return wk;
    const prescriptions = { ...(wk?.prescriptions || {}) };
    prescriptions[peId] = { ...(prescriptions[peId] || {}), ...rx };
    return { ...wk, prescriptions };
  });
  return { ...program, days, periodization: { ...per, weeks } };
}

// Returns { program, applied: [...descriptions], skipped: [...reasons], newExercises: [...] }
// newExercises are catalog entries that must be added to state.exercises.
// opts: { sessions, now } — needed by pin_week (the week is derived from the log).
export function applyProgramChanges(program, changes, exercises, catalog, opts = {}) {
  let prog = program;
  const applied = [], skipped = [], newExercises = [];
  const findDay = (name) => prog.days.find(d => norm(d.name) === norm(name)) || prog.days.find(d => nameScore(name, d.name) >= 0.6);
  const findPe = (day, name) => {
    let best = null, score = 0;
    for (const pe of day.exercises) {
      const s = Math.max(nameScore(name, pe.name), nameScore(name, prescriptionFor(prog, pe).name || ''));
      if (s > score) { best = pe; score = s; }
    }
    return score >= 0.6 ? best : null;
  };
  const libEx = (name) => {
    const ex = resolveExercise(name, [...exercises, ...newExercises], catalog);
    if (ex && ex.source === 'catalog' && !newExercises.some(n => n.id === ex.id) && !exercises.some(e => e.id === ex.id)) {
      newExercises.push(ex);
    }
    return ex;
  };
  const rxPatch = (c) => ({
    ...(c.sets != null ? { sets: c.sets } : {}),
    ...(c.rep_min != null ? { repMin: c.rep_min } : {}),
    ...(c.rep_max != null ? { repMax: Math.max(c.rep_max, c.rep_min ?? 0) } : {}),
    ...(c.rest_sec != null ? { rest: c.rest_sec } : {}),
    ...(c.weight_lb != null ? { targetWeight: c.weight_lb } : {}),
  });

  for (const c of changes || []) {
    if (c.op === 'pin_week') {
      const n = cycleOf(prog).weeks.length;
      const wk = Math.round(c.week || 0);
      if (!(wk >= 1)) { skipped.push('No week given'); continue; }
      if (n <= 1) { skipped.push('This program has no week cycle to move through'); continue; }
      if (wk > n) { skipped.push(`The cycle has ${n} weeks, not ${wk}`); continue; }
      const sessions = opts.sessions || [];
      const cur = programWeekInfo({ program: prog, sessions, now: opts.now });
      // Keep the absolute week inside the current run of the cycle.
      const target = cycleOf(prog).repeat ? cur.index - cur.week + (wk - 1) : wk - 1;
      prog = withCurrentWeek(prog, sessions, target, opts.now);
      applied.push(`Current week → ${cycleOf(prog).weeks[wk - 1]?.label || `week ${wk}`}`);
      continue;
    }
    if (c.op === 'set_tm') {
      const lifts = tmLifts(prog);
      const want = c.lift || c.exercise || '';
      let lift = lifts.find(t => norm(t.name) === norm(want)) || null;
      if (!lift) {
        let score = 0;
        for (const t of lifts) { const s = nameScore(want, t.name); if (s > score) { score = s; lift = t; } }
        if (score < 0.6) lift = null;
      }
      if (!lift) {
        // The exercise named may ride another lift's TM (e.g. close-grip bench on the bench TM).
        const day = findDay(c.day);
        const pe = day && findPe(day, c.exercise || want);
        if (pe && schemeOf(pe) === 'percent') lift = lifts.find(t => t.key === tmKeyOf(pe)) || null;
      }
      if (!lift) { skipped.push(`"${want}" has no training max (only percent-scheme lifts do)`); continue; }
      if (!(c.tm_lb > 0)) { skipped.push(`No training max given for ${lift.name}`); continue; }
      const tm = Math.round(c.tm_lb * 2) / 2;
      prog = { ...prog, tms: { ...(prog.tms || {}), [lift.key]: tm } };
      applied.push(`Training max · ${lift.name}: ${lift.tm > 0 ? `${lift.tm} → ` : ''}${tm} lb`);
      continue;
    }
    const day = findDay(c.day);
    if (!day) { skipped.push(`No day named "${c.day}"`); continue; }
    if (c.op === 'add') {
      const ex = libEx(c.exercise);
      if (!ex) { skipped.push(`Unknown exercise "${c.exercise}"`); continue; }
      const pe = {
        id: localId('pe_'), exerciseId: ex.id, name: ex.name, group: ex.group || 'Other',
        unit: ex.unit || 'reps', sets: c.sets || 3, repMin: c.rep_min || 8, repMax: c.rep_max || Math.max(c.rep_min || 8, 12),
        targetWeight: c.weight_lb || 0, rest: c.rest_sec || 120, yt: ex.yt || '', alternates: [],
      };
      prog = { ...prog, days: prog.days.map(d => d.id !== day.id ? d : { ...d, exercises: [...d.exercises, pe] }) };
      applied.push(`${day.name}: added ${ex.name}`);
      continue;
    }
    const pe = findPe(day, c.exercise);
    if (!pe) { skipped.push(`"${c.exercise}" isn't in ${day.name}`); continue; }
    if (c.op === 'set_scheme') {
      if (!SCHEMES[c.scheme]) { skipped.push(`${pe.name}: unknown scheme "${c.scheme}"`); continue; }
      const sc = schemeFromSpec({ ...c, sets: c.sets || pe.sets, rep_min: c.rep_min || pe.repMin });
      const nextPe = { ...pe };
      if (!sc) { delete nextPe.scheme; delete nextPe.schemeCfg; delete nextPe.schemeState; }
      else {
        nextPe.scheme = sc.scheme; nextPe.schemeCfg = { ...(pe.scheme === sc.scheme ? pe.schemeCfg || {} : {}), ...sc.cfg }; nextPe.schemeState = {};
        if (sc.scheme === 'percent') nextPe.targetWeight = 0;
      }
      if (c.sets) nextPe.sets = c.sets;
      if (c.rep_min) nextPe.repMin = c.rep_min;
      if (c.rep_max) nextPe.repMax = Math.max(c.rep_max, nextPe.repMin || 0);
      prog = { ...prog, days: prog.days.map(d => d.id !== day.id ? d : { ...d, exercises: d.exercises.map(e => e.id !== pe.id ? e : nextPe) }) };
      if (sc && sc.scheme === 'percent') {
        const key = tmKeyOf(nextPe);
        if (c.tm_lb > 0) prog = { ...prog, tms: { ...(prog.tms || {}), [key]: Math.round(c.tm_lb * 2) / 2 } };
      }
      applied.push(`${day.name}: ${pe.name} → ${SCHEMES[c.scheme].label}`);
      continue;
    }
    if (c.op === 'remove') {
      prog = { ...prog, days: prog.days.map(d => d.id !== day.id ? d : { ...d, exercises: d.exercises.filter(e => e.id !== pe.id) }) };
      applied.push(`${day.name}: removed ${pe.name}`);
    } else if (c.op === 'swap') {
      const ex = libEx(c.new_exercise || '');
      if (schemeOf(pe) === 'percent' && c.weight_lb != null) skipped.push(`${pe.name}: weight not set — it follows the training max (use set_tm)`);
      if (!ex) { skipped.push(`Unknown replacement "${c.new_exercise}"`); continue; }
      // Keep the slot id (periodized prescriptions are keyed by it); replace identity.
      prog = {
        ...prog, days: prog.days.map(d => d.id !== day.id ? d : {
          ...d, exercises: d.exercises.map(e => e.id !== pe.id ? e : {
            ...e, exerciseId: ex.id, name: ex.name, group: ex.group || e.group, unit: ex.unit || e.unit,
            yt: ex.yt || '', alternates: [], targetWeight: c.weight_lb ?? 0,
          }),
        }),
      };
      // Drop per-week name overrides for this slot so the new identity shows.
      if (prog.periodization?.weeks?.length) {
        prog = {
          ...prog, periodization: {
            ...prog.periodization,
            weeks: prog.periodization.weeks.map(wk => {
              const p = wk?.prescriptions?.[pe.id];
              if (!p || (p.name == null && p.yt == null && p.alternates == null)) return wk;
              const { name: _n1, yt: _y, alternates: _a, ...rest } = p;
              return { ...wk, prescriptions: { ...wk.prescriptions, [pe.id]: rest } };
            }),
          },
        };
      }
      const patch = rxPatch(c);
      if (schemeOf(pe) === 'percent') delete patch.targetWeight;
      if (Object.keys(patch).length) prog = withPrescription(prog, day.id, pe.id, patch);
      applied.push(`${day.name}: ${pe.name} → ${ex.name}`);
    } else {
      const patch = rxPatch(c);
      if (schemeOf(pe) === 'percent' && patch.targetWeight != null) {
        // Percent lifts are loaded from their training max: a target weight would be silently ignored.
        delete patch.targetWeight;
        skipped.push(`${pe.name}: weight not set — it follows the training max (use set_tm)`);
      }
      if (!Object.keys(patch).length) { if (!(c.weight_lb != null && schemeOf(pe) === 'percent')) skipped.push(`Nothing to update for ${pe.name}`); continue; }
      prog = withPrescription(prog, day.id, pe.id, patch);
      applied.push(`${day.name}: updated ${pe.name}`);
    }
  }
  return { program: prog, applied, skipped, newExercises };
}

// Scheme config for one spec exercise (see progression.js). → { scheme, cfg } | null
function schemeFromSpec(x) {
  const scheme = SCHEMES[x.scheme] ? x.scheme : 'double';
  if (scheme === 'double') return null;
  const cfg = {};
  if (scheme === 'linear') { cfg.failLimit = 3; if (x.inc_lb > 0) cfg.inc = x.inc_lb; }
  if (scheme === 'percent') cfg.wave = WAVES[x.wave] ? x.wave : 'w531';
  if (scheme === 'gzcl') cfg.tier = [1, 2, 3].includes(x.tier) ? x.tier : 1;
  if (scheme === 'rpe_topset') {
    cfg.topReps = x.top_reps || x.rep_min || 3;
    cfg.backoffs = x.backoffs ?? 3;
    cfg.backoffReps = x.backoff_reps || cfg.topReps;
    cfg.drop = 10;
    cfg.rpe = x.rpe >= 6 && x.rpe <= 10 ? x.rpe : 8;
  }
  if (scheme === 'rp_sets') {
    cfg.mev = Math.max(1, x.mev || x.sets || 2);
    cfg.mrv = Math.max(cfg.mev, x.mrv || cfg.mev + 3);
  }
  return { scheme, cfg };
}

// Week cycle for a spec: the model's own, else derived from its waves / mesocycle.
export function cycleForSpec(spec) {
  const c = spec.cycle;
  if (c && Array.isArray(c.weeks) && c.weeks.length) {
    return { repeat: c.repeat !== false, weeks: c.weeks.slice(0, 16).map((w, i) => ({ label: String(w.label || `Week ${i + 1}`).slice(0, 40), ...(w.deload ? { deload: true } : {}) })) };
  }
  const xs = (spec.days || []).flatMap(d => d.exercises || []);
  const pct = xs.find(x => x.scheme === 'percent');
  if (pct) {
    const w = WAVES[pct.wave] || WAVES.w531;
    const dl = w.deloadWeeks || [];
    if (w.weeks.length > 1) return { repeat: true, weeks: w.weeks.map((_, i) => (dl.includes(i) ? { label: 'Deload', deload: true } : { label: `Week ${i + 1}` })) };
  }
  if (xs.some(x => x.scheme === 'rp_sets')) {
    return { repeat: true, weeks: [{ label: 'Week 1 · 3 RIR' }, { label: 'Week 2 · 2 RIR' }, { label: 'Week 3 · 1 RIR' }, { label: 'Week 4 · 0 RIR' }, { label: 'Deload', deload: true }] };
  }
  // A multi-week block with no explicit structure: build up, last week deload, repeat.
  const n = Math.round(spec.weeks || 0);
  if (n >= 3 && n <= 16) return { repeat: true, weeks: Array.from({ length: n }, (_, i) => (i === n - 1 ? { label: 'Deload', deload: true } : { label: `Week ${i + 1}` })) };
  return null;
}

const WAVE_SHORT = { w531: '5/3/1', bbb: 'BBB 5×10', fsl: 'FSL 5×5', rtf: 'Rep-out wave', block6: '6-week block', peak12: '12-week peak', txVolume: 'Volume 5×5', txRecovery: 'Recovery 2×5', txIntensity: 'Intensity 1×5', dupHeavy: 'Heavy 5×3', dupMedium: 'Medium 4×6', dupLight: 'Light 3×8' };
/** Short scheme label for a spec exercise (approval card). '' for double progression. */
export function specSchemeLabel(x) {
  switch (x && x.scheme) {
    case 'linear': return `Linear +${x.inc_lb || 5} lb`;
    case 'percent': return `${WAVE_SHORT[x.wave] || (WAVES[x.wave] || WAVES.w531).label} · % of ${x.tm_of ? `${x.tm_of} TM` : 'TM'}`;
    case 'rpe_topset': return `Top set @RPE ${x.rpe || 8} + back-offs`;
    case 'gzcl': return `Tier ${x.tier || 1}`;
    case 'rp_sets': return `${x.mev || x.sets}→${x.mrv || (x.mev || x.sets) + 3} sets · RIR 3→0`;
    default: return '';
  }
}

// state (optional) seeds percent lifts' training maxes from logged history:
// 90 % of the best e1RM of the last three sessions, rounded to a loadable weight.
export function buildProgramFromSpec(spec, exercises, catalog, state = null) {
  const newExercises = [];
  const missing = [];
  const tms = {};
  const units = state?.prefs?.units || 'lb';
  const resolve = (name) => {
    const ex = resolveExercise(name, [...exercises, ...newExercises], catalog);
    if (ex && ex.source === 'catalog' && !newExercises.some(n => n.id === ex.id) && !exercises.some(e => e.id === ex.id)) newExercises.push(ex);
    return ex;
  };
  const days = (spec.days || []).map(d => ({
    id: localId('day_'),
    name: d.name,
    day: d.weekday,
    exercises: (d.exercises || []).map(x => {
      const ex = resolve(x.exercise);
      if (!ex) { missing.push(x.exercise); }
      const pe = {
        id: localId('pe_'), exerciseId: ex ? ex.id : null, name: ex ? ex.name : x.exercise,
        group: ex ? ex.group : 'Other', unit: (ex && ex.unit) || 'reps',
        sets: x.sets, repMin: x.rep_min, repMax: Math.max(x.rep_max, x.rep_min),
        targetWeight: x.weight_lb || 0, rest: x.rest_sec, yt: (ex && ex.yt) || '', alternates: [],
      };
      const sc = schemeFromSpec(x);
      if (sc) {
        pe.scheme = sc.scheme;
        pe.schemeCfg = sc.cfg;
        pe.schemeState = {};
        if (sc.scheme === 'percent') {
          pe.targetWeight = 0;
          if (x.tm_of) {
            const ref = resolve(x.tm_of);
            if (ref && ref.id !== pe.exerciseId) { pe.schemeCfg.tmKey = ref.id; pe.schemeCfg.tmName = ref.name; }
          }
          const key = pe.schemeCfg.tmKey || pe.exerciseId;
          if (key && x.tm_lb > 0 && !(tms[key] > 0)) tms[key] = roundTo(x.tm_lb, units);
          if (key && !(key in tms) && state) {
            const h = exerciseHistory(state, key).slice(-3);
            const best = Math.max(0, ...h.map(p => p.e1rm || 0));
            tms[key] = best > 0 ? roundTo(best * 0.9, units) : 0;
          }
        }
      }
      return pe;
    }),
  }));
  const cycle = cycleForSpec(spec);
  const realTms = Object.fromEntries(Object.entries(tms).filter(([, v]) => v > 0));
  return {
    program: {
      name: spec.name, author: 'AI Coach', weeks: spec.weeks || 8, currentWeek: 1,
      split: `${days.length} days · ${spec.summary ? spec.summary.split('.')[0].slice(0, 60) : 'Custom'}`,
      summary: spec.summary, days,
      ...(cycle ? { cycle } : {}),
      ...(Object.keys(realTms).length ? { tms: realTms } : {}),
      startedAt: Date.now(),
    },
    newExercises, missing,
  };
}

// ——— Validating proposals before they are shown ————————————————————————
/**
 * validateProposal(toolName, input, { state, program, catalog, now }) → { ok, errors[] }
 * Every program change is dry-run against the real program (and progression.js);
 * a new program must resolve every exercise and keep its cycle consistent with
 * its waves. The coach loop returns the errors to the model instead of showing
 * a card that can't be applied.
 */
export function validateProposal(name, input = {}, { state = {}, program = null, catalog = null, now = Date.now() } = {}) {
  const errors = [];
  const exercises = state.exercises || [];
  if (name === 'propose_program_changes') {
    if (!program) return { ok: false, errors: ['There is no active program. Use propose_new_program to create one.'] };
    const changes = Array.isArray(input.changes) ? input.changes : [];
    if (!changes.length) errors.push('No changes given.');
    changes.forEach((c, i) => {
      const r = applyProgramChanges(program, [c], exercises, catalog, { sessions: state.sessions || [], now });
      if (!r.applied.length || r.skipped.length) errors.push(`Change ${i + 1} (${c.op}${c.exercise ? ' ' + c.exercise : ''}${c.day ? ' on ' + c.day : ''}): ${r.skipped.join('; ') || 'nothing to apply'}`);
    });
  } else if (name === 'propose_new_program') {
    const days = Array.isArray(input.days) ? input.days : [];
    if (!days.length) errors.push('The program has no days.');
    const r = buildProgramFromSpec(input, exercises, catalog, null);
    if (r.missing.length) errors.push(`Not in the exercise library: ${r.missing.join(', ')}. Use names returned by search_exercises.`);
    for (const d of days) for (const x of d.exercises || []) {
      if (x.rep_min > x.rep_max) errors.push(`${x.exercise}: rep_min is above rep_max.`);
      if (x.scheme === 'rp_sets' && x.mev && x.mrv && x.mev > x.mrv) errors.push(`${x.exercise}: mev must not exceed mrv.`);
      if (x.scheme && !SCHEMES[x.scheme]) errors.push(`${x.exercise}: unknown scheme "${x.scheme}".`);
      if (x.scheme === 'percent' && x.wave && !WAVES[x.wave]) errors.push(`${x.exercise}: unknown wave "${x.wave}".`);
      if (x.tm_of && !resolveExercise(x.tm_of, exercises, catalog)) errors.push(`${x.exercise}: tm_of "${x.tm_of}" is not a known exercise.`);
    }
    const n = input.cycle?.weeks?.length || 0;
    if (n) {
      for (const x of days.flatMap(d => d.exercises || [])) {
        if (x.scheme !== 'percent') continue;
        const len = (WAVES[x.wave] || WAVES.w531).weeks.length;
        if (len > 1 && n % len !== 0) { errors.push(`The cycle has ${n} weeks but the ${x.wave || 'w531'} wave on ${x.exercise} is ${len} weeks long; make the cycle a multiple of ${len}.`); break; }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

// ——— "Ask about this" ————————————————————————————————————————————————————

const fmtDay = (d) => {
  const t = typeof d === 'number' ? d : Date.parse(d);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
};

/**
 * What the coach shows and sends for a context opened from elsewhere in the app.
 * → { kicker, label, sub, text (the "[context]" block for the model, or null),
 *     suggestions: [question…], prefill? } | null
 */
export function contextBlockFor(ctx, state) {
  if (!ctx || !ctx.kind) return null;
  const exName = (id) => (state?.exercises || []).find(e => e.id === id)?.name || null;
  const name = ctx.exerciseId ? (exName(ctx.exerciseId) || ctx.label || 'this exercise') : (ctx.label || '');
  switch (ctx.kind) {
    case 'exercise': return {
      kicker: 'Exercise', label: name, sub: null,
      text: `[context] The lifter opened the coach from the ${name} exercise page (look it up with get_exercise_history / get_exercise_guide as needed).`,
      suggestions: [`How is my ${name} progressing?`, `What should my next ${name} session be?`, `Give me form cues for ${name}`, `What's a good substitute for ${name}?`],
    };
    case 'pr': {
      const sub = ctx.w && ctx.reps ? `${ctx.w} lb × ${ctx.reps}${ctx.date ? ` · ${fmtDay(ctx.date)}` : ''}` : null;
      return {
        kicker: 'Personal record', label: name, sub,
        text: `[context] The lifter just set a personal record on ${name}${sub ? `: ${sub}` : ''}. Check get_exercise_history before answering.`,
        suggestions: ['What does this PR mean for my next sessions?', `How do I keep my ${name} climbing?`, 'Should my training max or target weight go up?'],
      };
    }
    case 'stall': return {
      kicker: 'Stalled lift', label: name, sub: null,
      text: `[context] The app flagged ${name} as stalled: its estimated 1RM hasn't improved over its last several sessions. Check get_exercise_history and get_program before answering.`,
      suggestions: [`Why has my ${name} stalled?`, `Give me a plan to break this ${name} plateau`, `Should I swap ${name} for a few weeks?`],
    };
    case 'session': {
      const s = (state?.sessions || []).find(x => x.id === ctx.sessionId);
      const label = s ? (s.name || 'Workout') : (ctx.label || 'Workout');
      const sets = s ? (s.sets || []).filter(x => x.type !== 'warmup').length : null;
      const sub = s ? `${fmtDay(s.startedAt)}${sets != null ? ` · ${sets} working sets` : ''}` : null;
      return {
        kicker: 'Workout', label, sub,
        text: `[context] The lifter opened the coach from the summary of their workout "${label}"${sub ? ` (${sub})` : ''}. Look it up with get_recent_workouts.`,
        suggestions: ['How did this workout go?', 'What should I change next time?', 'Was that enough volume for my goals?'],
      };
    }
    case 'muscle': return {
      kicker: 'Weekly volume', label: ctx.muscle || 'Volume', sub: null,
      text: `[context] The lifter is asking about their ${ctx.muscle || ''} training volume. Check get_weekly_volume and get_program.`,
      suggestions: [`Am I doing enough ${(ctx.muscle || '').toLowerCase()} volume?`, `Fix my ${(ctx.muscle || '').toLowerCase()} volume in my program`, 'Which exercises would add it best?'],
    };
    case 'program': {
      const p = (state?.programs || []).find(x => x.id === ctx.programId);
      return {
        kicker: 'Program', label: p?.name || ctx.label || 'Your program', sub: null,
        text: `[context] The lifter opened the coach from their program "${p?.name || ''}". Check get_program.`,
        suggestions: ['Review my program', 'Is my weekly volume balanced?', 'How should this program progress?'],
      };
    }
    case 'free':
    default:
      return ctx.prompt ? { kicker: null, label: null, sub: null, text: null, suggestions: [], prefill: ctx.prompt } : null;
  }
}

// ——— Weekly review (structured, applicable) ————————————————————————————————

// Structured outputs need every field required, so a change is flat: unused
// numbers are 0 and unused strings are ''.
const reviewChange = {
  type: 'object', additionalProperties: false,
  properties: {
    op: { type: 'string', enum: ['update', 'swap', 'add', 'remove', 'set_tm'] },
    day: { type: 'string', description: 'Program day name exactly as in PROGRAM.' },
    exercise: { type: 'string', description: 'Exercise name exactly as in PROGRAM (or the exercise to add).' },
    new_exercise: { type: 'string', description: "swap only: the replacement; '' otherwise." },
    sets: { type: 'integer', description: '0 = unchanged.' },
    rep_min: { type: 'integer', description: '0 = unchanged.' },
    rep_max: { type: 'integer', description: '0 = unchanged.' },
    weight_lb: { type: 'number', description: '0 = unchanged. Never for percent lifts.' },
    tm_lb: { type: 'number', description: 'set_tm only: new training max; 0 otherwise.' },
    reason: { type: 'string', description: 'One short sentence citing the data.' },
  },
  required: ['op', 'day', 'exercise', 'new_exercise', 'sets', 'rep_min', 'rep_max', 'weight_lb', 'tm_lb', 'reason'],
};

export const WEEKLY_REVIEW_SCHEMA = {
  type: 'object', additionalProperties: false,
  properties: {
    headline: { type: 'string', description: 'Up to 8 words.' },
    summary: { type: 'string', description: '2–4 short sentences on how the week went, citing numbers from the data.' },
    wins: { type: 'array', items: { type: 'string' }, description: '0–3 short wins backed by the data.' },
    focus: { type: 'string', description: 'The one thing to focus on next week, one sentence.' },
    suggestions: {
      type: 'array',
      description: '0–3 applicable suggestions. Fewer, stronger suggestions beat filler; an empty list is fine.',
      items: {
        type: 'object', additionalProperties: false,
        properties: {
          kind: { type: 'string', enum: ['program_change', 'volume', 'deload'] },
          title: { type: 'string', description: 'Card title, up to 6 words.' },
          why: { type: 'string', description: 'One sentence citing the data that supports it.' },
          muscle: { type: 'string', description: "volume only: the muscle group; '' otherwise." },
          changes: { type: 'array', items: reviewChange, description: 'program_change / volume: the edits. deload: empty.' },
        },
        required: ['kind', 'title', 'why', 'muscle', 'changes'],
      },
    },
  },
  required: ['headline', 'summary', 'wins', 'focus', 'suggestions'],
};

const REVIEW_SYSTEM = `You are an evidence-based strength coach writing the weekly check-in inside a workout app. Use ONLY the data provided; never invent numbers, sessions or feelings. Weights are in lb.

Voice and grounding rules (follow all of them):
- Write to the lifter in the second person ("you"). Never use their name or the third person.
- The review covers exactly one window: the dates in REVIEW WINDOW. Say "these 7 days" or name the dates; never "this week so far", never workouts outside the window.
- Every claim carries a number from the data (workouts, sets, reps, loads, dates, % change). No praise or alarm words without a number, no hype ("crushing", "amazing", "critical").
- Only say every planned workout was done when WORKOUTS done >= planned.
- Volume: call a muscle low or high only when VOLUME marks it LOW or HIGH (4-week average against its landmarks). A single short week is not a volume problem.
- Body weight: state the change only. Never infer fat loss, muscle gain or body composition from scale weight.
- A PR or stall is only what LIFTS shows (best e1RM in the window vs the 4 weeks before it).

Return JSON that matches the schema:
- headline, summary, wins, focus: how the window went (consistency, volume, PRs and plateaus, readiness) and the single most useful focus for next week.
- suggestions: up to 3 changes the lifter can apply with one tap. Each must be supported by the data:
  - "program_change": edits to PROGRAM (stalled lift → swap or rep-range change, a missed + set on a percent lift → set_tm about 10 % lower, etc.).
  - "volume": rebalance a muscle VOLUME marks LOW (below MEV) or HIGH (above MRV) — add or remove sets/exercises on specific days. Set "muscle".
  - "deload": only when DELOAD SIGNAL is yes, or the data clearly shows accumulated fatigue (several stalled lifts plus low readiness). No changes.
- Use day and exercise names exactly as they appear in PROGRAM. For percent-scheme lifts change the training max with set_tm, never weight_lb. Unused numeric fields are 0 and unused strings are "".
- If there is no program, return no program_change or volume suggestions. If the window had no training, say so plainly and keep suggestions to what the data supports.`;

const DAY_MS = 86400000;
const shortDay = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * The weekly review's one time window: the 7 full days ending yesterday
 * (local), so a review run on Oct 1 covers Sep 24 – Sep 30 and today's
 * unfinished training never counts. → { kind, start, end (exclusive), startDate, endDate, label }
 */
export function reviewWindow(now = Date.now()) {
  const end = new Date(now); end.setHours(0, 0, 0, 0);
  const start = new Date(end); start.setDate(start.getDate() - 7);
  const s = start.getTime(), e = end.getTime();
  return { kind: 'last7', start: s, end: e, startDate: localDateStr(s), endDate: localDateStr(e - 1), label: `${shortDay(s)} – ${shortDay(e - 1)}` };
}
const hardSets = (s) => (s.sets || []).filter(x => x && x.type !== 'warmup' && (x.reps > 0 || x.dur > 0 || x.sec > 0 || x.w > 0));

/**
 * Everything the weekly review says, computed on one window (reviewWindow).
 * → { window, stats: { workouts, planned, sets, prevSets, low[], inRange, tracked } (the card's
 *     stat row), sessions[], volume[], lifts[], program: { planned, done, rotation }, bodyWeight }
 */
export function reviewData(state, program, now = Date.now()) {
  const win = reviewWindow(now);
  const idx = indexExercises(state);
  const nameOf = (id) => idx[id]?.name || 'Exercise';
  const done = completedSessions(state);
  const inWin = (s, a = win.start, b = win.end) => s.startedAt >= a && s.startedAt < b;
  const sessions = done.filter(s => inWin(s));
  const prevSessions = done.filter(s => inWin(s, win.start - 7 * DAY_MS, win.start));
  // Hard sets per muscle: this window and the 3 windows before it (4-week average).
  const first = done.length ? done[0].startedAt : win.start;
  const windows = [0, 1, 2, 3].map(k => ({ a: win.start - k * 7 * DAY_MS, b: win.end - k * 7 * DAY_MS }));
  const per = windows.map(w => muscleSets(state, w.a, w.b, idx));
  const weeksAvail = windows.filter(w => w.b > first && w.a >= first - 6 * DAY_MS).length;
  const muscles = TRACKED_MUSCLES.filter(m => per.some(p => p[m] > 0) || VOLUME_LANDMARKS[m].mev > 0);
  const volume = muscles.map(m => {
    const lm = VOLUME_LANDMARKS[m];
    const sets = +(per[0][m] || 0).toFixed(1);
    const avg4 = weeksAvail ? +(per.slice(0, weeksAvail).reduce((a, p) => a + (p[m] || 0), 0) / weeksAvail).toFixed(1) : 0;
    let status = 'ok';
    if (weeksAvail >= 2 && lm.mev > 0 && avg4 < lm.mev) status = 'low';
    else if (weeksAvail >= 2 && avg4 > lm.mrv) status = 'high';
    return { muscle: m, sets, avg4, weeks: weeksAvail, mev: lm.mev, mav: lm.mav, mrv: lm.mrv, status };
  });
  const tracked = volume.filter(v => v.mev > 0 && v.weeks >= 2);
  const low = tracked.filter(v => v.status === 'low').sort((a, b) => (a.avg4 / a.mev) - (b.avg4 / b.mev)).map(v => ({ muscle: v.muscle, avg: Math.round(v.avg4), mev: v.mev }));
  // Lifts: best e1RM in the window vs the 4 weeks before it.
  const lifts = [];
  const ids = [...new Set(sessions.flatMap(s => hardSets(s).map(x => x.exerciseId)))];
  for (const id of ids) {
    const h = exerciseHistory(state, id);
    const cur = h.filter(p => p.date >= win.start && p.date < win.end);
    const before = h.filter(p => p.date >= win.start - 28 * DAY_MS && p.date < win.start);
    if (!cur.length || !(Math.max(...cur.map(p => p.e1rm || 0)) > 0)) continue;
    const best = cur.reduce((a, p) => ((p.e1rm || 0) > (a.e1rm || 0) ? p : a));
    const prevBest = before.length ? Math.max(...before.map(p => p.e1rm || 0)) : 0;
    lifts.push({
      exercise: nameOf(id), best_set: `${best.bestW} × ${best.bestReps}${best.topRpe ? ` @${best.topRpe}` : ''}`, e1rm: Math.round(best.e1rm),
      prev_4wk_e1rm: prevBest > 0 ? Math.round(prevBest) : null,
      change_pct: prevBest > 0 ? Math.round(((best.e1rm - prevBest) / prevBest) * 1000) / 10 : null,
    });
  }
  lifts.sort((a, b) => Math.abs(b.change_pct || 0) - Math.abs(a.change_pct || 0));
  // The program's rotation, so "N of M" means the same thing as Home.
  let prog = null;
  if (program) {
    const planned = (program.days || []).length;
    const doneIn = sessions.filter(s => s.programId === program.id).length;
    const nx = nextWorkout({ ...state, programs: [program], activeProgramId: program.id }, program.id, now);
    const left = (program.days || []).filter(d => !nx.doneDayIds.includes(d.id) && !nx.skippedDayIds.includes(d.id)).map(d => d.name);
    prog = { planned, done: doneIn, rotation: { week: nx.weekIndex + 1, label: nx.weekLabel || '', done: nx.doneDayIds.length, left } };
  }
  const bw = (state.bodyEntries || []).filter(e => e && e.date && e.weight > 0).sort((a, b) => (a.date < b.date ? -1 : 1));
  const bwIn = bw.filter(e => e.date >= win.startDate && e.date <= win.endDate);
  const bodyWeight = bwIn.length >= 2 ? { from: bwIn[0].weight, to: bwIn[bwIn.length - 1].weight, change: round1(bwIn[bwIn.length - 1].weight - bwIn[0].weight) } : null;
  const sets = sessions.reduce((a, s) => a + hardSets(s).length, 0);
  const prevSets = prevSessions.reduce((a, s) => a + hardSets(s).length, 0);
  return {
    window: win,
    stats: {
      workouts: sessions.length, planned: program?.days?.length || null,
      sets, prevSets: prevSessions.length ? prevSets : null,
      low, inRange: tracked.length - low.length - tracked.filter(v => v.status === 'high').length, tracked: tracked.length,
      window: win.label,
    },
    sessions: sessions.map(s => {
      const groups = {};
      for (const st of hardSets(s)) {
        const nm = st.performedName || idx[st.exerciseId]?.name || st.exerciseName || 'Exercise';
        (groups[nm] = groups[nm] || []).push(`${st.w}x${st.reps}${st.rpe ? '@' + st.rpe : ''}`);
      }
      return `${fmtDate(s.startedAt)} ${s.name || 'Workout'}${s.feel ? ` (felt ${s.feel}/5)` : ''}: ${Object.entries(groups).map(([k, v]) => `${k} ${v.join(', ')}`).join('; ') || 'no working sets'}`;
    }),
    volume, lifts: lifts.slice(0, 10), program: prog, bodyWeight,
  };
}

/** System + user prompt for the weekly review (all numbers come from the log, one window). */
export function weeklyReviewPrompt(state, program, now = Date.now()) {
  const data = reviewData(state, program, now);
  const w = data.window;
  const dl = deloadAdvice(state, now);
  const p = data.program;
  const prompt = [
    `REVIEW WINDOW: ${w.label} (${w.startDate} to ${w.endDate}, the 7 days ending yesterday). Every number below covers this window unless it says otherwise.`,
    '',
    'LIFTER (on-device, for context only):',
    coachContext(state, program, now, { review: true }),
    '',
    'PROGRAM (use these exact day and exercise names):',
    JSON.stringify(programForModel(program, state.exercises || [], state.sessions || [], now)),
    '',
    `WORKOUTS: ${data.stats.workouts} in the window${p ? ` (the program has ${p.planned} days per rotation; ${p.done} of the window's workouts were from it). Current rotation: week ${p.rotation.week}, ${p.rotation.done} of ${p.planned} days done${p.rotation.left.length ? `, still to do: ${p.rotation.left.join(', ')}` : ''}.` : '.'} Hard sets: ${data.stats.sets}${data.stats.prevSets != null ? ` (the 7 days before: ${data.stats.prevSets})` : ''}.`,
    '',
    'VOLUME (hard sets per muscle: sets = this window, avg4 = average of this window and the 3 before it; status LOW/HIGH = avg4 against MEV/MRV, needs 2+ weeks of data):',
    JSON.stringify(data.volume.filter(v => v.sets > 0 || v.avg4 > 0 || v.status !== 'ok').map(v => ({ muscle: v.muscle, sets: v.sets, avg4: v.avg4, mev: v.mev, mrv: v.mrv, status: v.status.toUpperCase() }))),
    '',
    'LIFTS (best e1RM in the window vs the best in the 4 weeks before it):',
    data.lifts.length ? JSON.stringify(data.lifts) : 'none',
    '',
    'SESSIONS IN THE WINDOW:',
    data.sessions.length ? data.sessions.join('\n') : 'No workouts in the window.',
    '',
    `BODY WEIGHT IN THE WINDOW: ${data.bodyWeight ? `${data.bodyWeight.from} → ${data.bodyWeight.to} (${data.bodyWeight.change >= 0 ? '+' : ''}${data.bodyWeight.change})` : 'not enough entries'}`,
    '',
    `DELOAD SIGNAL: ${dl?.recommend ? `yes — ${(dl.reasons || []).join(' ')}` : 'no'}`,
  ].join('\n');
  return { system: REVIEW_SYSTEM, prompt, window: w, stats: data.stats };
}

function normalizeChange(c) {
  if (!c || typeof c !== 'object') return null;
  const out = { op: c.op, day: String(c.day || ''), exercise: String(c.exercise || ''), reason: String(c.reason || '') };
  if (c.new_exercise) out.new_exercise = String(c.new_exercise);
  if (c.sets > 0) out.sets = Math.min(12, Math.round(c.sets));
  if (c.rep_min > 0) out.rep_min = Math.min(100, Math.round(c.rep_min));
  if (c.rep_max > 0) out.rep_max = Math.min(100, Math.round(c.rep_max));
  if (c.weight_lb > 0) out.weight_lb = c.weight_lb;
  if (c.tm_lb > 0) out.tm_lb = c.tm_lb;
  return out.day && out.exercise ? out : null;
}

/**
 * Cleans a model review: every program edit is dry-run against the real
 * program (applyProgramChanges) and kept only if it applies; suggestions left
 * with nothing applicable are dropped. Deload is offered only with a program
 * that isn't already on a deload week.
 */
export function normalizeReview(raw, { state, program, catalog = null, now = Date.now() } = {}) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const suggestions = [];
  (Array.isArray(r.suggestions) ? r.suggestions : []).slice(0, 4).forEach((sg, i) => {
    if (!sg || typeof sg !== 'object') return;
    const base = { id: `sg${i}`, title: String(sg.title || '').slice(0, 80), why: String(sg.why || '').slice(0, 300) };
    if (!base.title) return;
    if (sg.kind === 'deload') {
      if (!program || program.deloadWeek === weekStart(now)) return;
      suggestions.push({ ...base, kind: 'deload', changes: [] });
      return;
    }
    if (!program) return;
    const changes = (Array.isArray(sg.changes) ? sg.changes : []).map(normalizeChange).filter(Boolean)
      .filter(c => applyProgramChanges(program, [c], state?.exercises || [], catalog).applied.length > 0);
    if (!changes.length) return;
    suggestions.push({ ...base, kind: sg.kind === 'volume' ? 'volume' : 'program_change', muscle: String(sg.muscle || ''), changes });
  });
  return {
    headline: String(r.headline || 'Your week').slice(0, 80),
    summary: String(r.summary || ''),
    wins: (Array.isArray(r.wins) ? r.wins : []).filter(w => typeof w === 'string' && w.trim()).slice(0, 3),
    focus: String(r.focus || ''),
    suggestions: suggestions.slice(0, 3),
    at: now,
    // The one window every number refers to, and its on-device stat row.
    window: (() => { const w = reviewWindow(now); return { start: w.start, end: w.end, label: w.label }; })(),
    stats: state ? reviewData(state, program, now).stats : undefined,
  };
}
